import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronLeft, ChevronRight, Clapperboard, Clapperboard as ClapperIcon, LayoutDashboard, LogOut, MapPin, Settings, Star, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

const COMPANY_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Projects" },
  { to: "/crew", icon: Users, label: "People Database" },
  { to: "/locations", icon: MapPin, label: "Locations Database" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(true);
  const [projectName, setProjectName] = useState("");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect "inside a project" context. The call-sheet editor/preview URLs live
  // at /callsheets/:id/… — resolve the owning project by fetching the callsheet.
  const projectMatch = location.pathname.match(/^\/projects\/([^/]+)/);
  const callsheetMatch = location.pathname.match(/^\/callsheets\/([^/]+)\/(edit|preview|distribute)/);
  const explicitProjectId = projectMatch?.[1] || null;
  const callsheetId = callsheetMatch?.[1] || null;

  const [resolvedProjectId, setResolvedProjectId] = useState(null);
  const projectId = explicitProjectId || resolvedProjectId;

  useEffect(() => {
    if (explicitProjectId || !callsheetId) {
      setResolvedProjectId(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/callsheets/${callsheetId}`, ax())
      .then(({ data }) => { if (!cancelled) setResolvedProjectId(data?.project_id || null); })
      .catch(() => { if (!cancelled) setResolvedProjectId(null); });
    return () => { cancelled = true; };
  }, [callsheetId, explicitProjectId]);

  useEffect(() => {
    if (!projectId) {
      setProjectName("");
      return;
    }
    let cancelled = false;
    axios.get(`${API}/projects/${projectId}`, ax())
      .then(({ data }) => { if (!cancelled) setProjectName(data?.name || ""); })
      .catch(() => { if (!cancelled) setProjectName(""); });
    return () => { cancelled = true; };
  }, [projectId]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const projectNav = projectId ? [
    { to: `/projects/${projectId}`, icon: ClapperIcon, label: "Project Dashboard" },
    { to: `/projects/${projectId}/crew`, icon: Users, label: "Project Crew" },
    { to: `/projects/${projectId}/talent`, icon: Star, label: "Project Talent" },
    { to: `/projects/${projectId}/locations`, icon: MapPin, label: "Project Locations" },
  ] : [];

  return (
    <div className="flex h-screen overflow-hidden bg-[#0D0F12]">
      <aside
        className={`${open ? "w-60" : "w-[60px]"} hidden flex-shrink-0 flex-col border-r border-[#28303F] bg-[#14181F] transition-all duration-300 md:flex`}
      >
        <div className="flex min-h-[64px] items-center gap-3 border-b border-[#28303F] px-4 py-4">
          <Clapperboard className="flex-shrink-0 text-amber-400" size={22} />
          {open && <span className="font-[Outfit] text-lg font-bold tracking-tight text-white">Get2Set</span>}
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {projectId ? (
            <>
              <NavLink
                to="/dashboard"
                data-testid="nav-back-to-projects"
                className="mb-2 flex items-center gap-3 rounded-lg px-3 py-2 text-gray-500 transition-colors hover:text-amber-400"
              >
                <ArrowLeft size={14} className="flex-shrink-0" />
                {open && <span className="text-xs">All projects</span>}
              </NavLink>
              {open && projectName && (
                <div className="mb-3 px-3 py-1.5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400/70">Inside</p>
                  <p className="truncate font-[Outfit] text-sm font-bold text-white" data-testid="sidebar-project-name">{projectName}</p>
                </div>
              )}
              {projectNav.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === `/projects/${projectId}`}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all duration-200 ${
                      isActive
                        ? "border-amber-500/30 bg-amber-500/15 text-amber-400"
                        : "border-transparent text-gray-400 hover:bg-[#1A1F29] hover:text-white"
                    }`
                  }
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {open && <span className="truncate text-sm font-medium">{label}</span>}
                </NavLink>
              ))}
              <div className="mx-3 my-3 border-t border-[#28303F]" />
              <NavLink
                to="/settings"
                data-testid="nav-settings"
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all duration-200 ${
                    isActive
                      ? "border-amber-500/30 bg-amber-500/15 text-amber-400"
                      : "border-transparent text-gray-400 hover:bg-[#1A1F29] hover:text-white"
                  }`
                }
              >
                <Settings size={18} className="flex-shrink-0" />
                {open && <span className="truncate text-sm font-medium">Settings</span>}
              </NavLink>
            </>
          ) : (
            COMPANY_NAV.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all duration-200 ${
                    isActive
                      ? "border-amber-500/30 bg-amber-500/15 text-amber-400"
                      : "border-transparent text-gray-400 hover:bg-[#1A1F29] hover:text-white"
                  }`
                }
              >
                <Icon size={18} className="flex-shrink-0" />
                {open && <span className="truncate text-sm font-medium">{label}</span>}
              </NavLink>
            ))
          )}
        </nav>

        <div className="border-t border-[#28303F] p-3">
          {open && (
            <div className="mb-1 px-3 py-2">
              <p className="truncate text-xs text-gray-500">{user?.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-gray-400 transition-all duration-200 hover:bg-red-500/10 hover:text-red-400"
          >
            <LogOut size={18} className="flex-shrink-0" />
            {open && <span className="text-sm">Logout</span>}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-gray-600 transition-all duration-200 hover:text-gray-400"
          >
            {open ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            {open && <span className="text-xs text-gray-600">Collapse</span>}
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
